'use client';

import { Button, ButtonType } from '@/components/ui/button';
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { MdMenu } from 'react-icons/md';

import type { Reservation } from '@parkingbase/ecommerce-api-dev';

import { Avatar } from '@/components/ui/avatar/Avatar';
import { AvatarSize, AvatarVariant } from '@/components/ui/avatar/Avatar.types';
import { ActiveSessionIndicator } from '@/components/layout/header/active-session-indicator';
import { ActiveSessionsPopup } from '@/components/layout/header/active-sessions-popup';
import { ReservationPassModal } from '@/components/myaccount/reservation-pass-modal';
import { ReleaseRefreshBanner } from '@/components/shared/release-refresh-banner';
import { useAnnounce } from '@/hooks/useAnnounce';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useReleaseCheck } from '@/hooks/useReleaseCheck';
import { Link, usePathname } from '@/lib/app-router';
import { cn } from '@/lib/styleUtils';
import { SiteHeaderDrawer } from './SiteHeaderDrawer';
import { SiteHeaderZone } from './SiteHeaderZone';
import {
  avatarButtonClasses,
  centerSlotClasses,
  leftClusterClasses,
  logoLinkClasses,
  menuButtonClasses,
  mobileCenterClasses,
  rightClusterClasses,
  signInClasses,
  siteHeaderBarClasses,
} from './SiteHeader.style';
import {
  SITE_HEADER_DESKTOP_QUERY,
  SITE_HEADER_WIDE_QUERY,
  type OpenBy,
  type SiteHeaderProps,
} from './SiteHeader.types';

/**
 * The site header drawn from the published navigation (PB-9032 desktop and tablet, PB-9033 phone).
 *
 * ## Invariants:
 * - During a live session (`hasLiveSession` with a session chip to show) the CENTER zone is NOT
 *   rendered: it is unmounted, never hidden with CSS, so assistive tech and tests cannot find it.
 *   The chip takes its column. When that transition happens while the viewer is using the page, it
 *   is announced once (never on first mount with a session already live) and focus that was inside
 *   CENTER moves to the chip.
 * - Desktop vs phone is decided by `useMediaQuery`, never by CSS-toggling: exactly one of the two
 *   bars (and the drawer, phone only) is in the DOM, so every selector matches once at any width.
 * - `isLoading` draws a logo-only bar at the same height, so there is no flash of the old menu.
 * - No caller class decides a root's display or layout model: each root's `grid` / `flex` is its
 *   own. "Hidden on phones" is the `hideOnPhone` prop, which renders no bar (desktop, phone or
 *   skeleton) below 768 px; a caller's `tw:hidden tw:md:flex` would make tailwind-merge drop the
 *   desktop `tw:grid` and collapse the `1fr auto 1fr` columns (PB-9032 R1-F1).
 * - Both rendered bars carry `data-cy="site-header"` with `data-layout="CLASSIC"` (Surface Spec);
 *   the skeleton is `site-header-skeleton` only.
 * - Keeps the legacy Header's contract for the pieces other code depends on: `header-logo-link`
 *   (with the same-path re-entry of PB-4971), `header-avatar-button`, the sessions popup, the View
 *   Pass modal and the release check. The logo handler below deliberately mirrors `Header.tsx`;
 *   change both together.
 */
export function SiteHeader({
  logo,
  logoHref = '/',
  user,
  onAvatarClick,
  avatarRef,
  isMenuOpen,
  activeSessionIndicator,
  activeSessionsPopup,
  zones,
  showLogin,
  loginHref,
  hasLiveSession,
  isLoading = false,
  hideOnPhone = false,
  className,
}: SiteHeaderProps) {
  const t = useTranslations('SiteHeader');
  const tSite = useTranslations('SitePage');
  const tNav = useTranslations('Navigation');
  const pathname = usePathname();
  const isDesktop = useMediaQuery(SITE_HEADER_DESKTOP_QUERY);
  const isWide = useMediaQuery(SITE_HEADER_WIDE_QUERY);
  const announce = useAnnounce();
  useReleaseCheck();

  const pillRef = useRef<HTMLElement>(null);
  const centerRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const drawerId = useId();

  const showChip = hasLiveSession && !!activeSessionIndicator;

  // ---- Logo: same-path re-entry (mirrors Header.tsx, PB-4971) ---------------------------------
  const handleLogoClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      const [logoPath, logoQuery] = logoHref.split('?');
      if (pathname !== logoPath) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      window.location.assign(
        logoQuery ? `${window.location.pathname}?${logoQuery}` : window.location.pathname
      );
    },
    [pathname, logoHref]
  );

  // ---- View Pass modal for transient tickets, opened from the sessions popup ------------------
  const [passReservation, setPassReservation] = useState<Reservation | null>(null);
  const handleViewPass = useCallback(
    (reservation: Reservation) => setPassReservation(reservation),
    []
  );
  const handleClosePass = useCallback(() => setPassReservation(null), []);

  // ---- Dropdown state: one top-level panel at a time ------------------------------------------
  const [open, setOpen] = useState<{ id: string; by: OpenBy } | null>(null);
  const handleOpenRequest = useCallback((id: string, by: OpenBy) => setOpen({ id, by }), []);
  const handleCloseRequest = useCallback(
    (id: string) => setOpen((current) => (current?.id === id ? null : current)),
    []
  );

  // ---- Phone drawer ---------------------------------------------------------------------------
  const [menuRequested, setMenuRequested] = useState(false);
  const menuOpen = menuRequested && !isDesktop;
  const closeMenu = useCallback((returnFocus: boolean) => {
    setMenuRequested(false);
    if (returnFocus) toggleRef.current?.focus();
  }, []);

  // A route change closes every open panel and the drawer.
  const [seenPath, setSeenPath] = useState(pathname);
  if (seenPath !== pathname) {
    setSeenPath(pathname);
    setOpen(null);
    setMenuRequested(false);
  }

  // ---- Live-session swap: announce once, move focus out of the removed CENTER -----------------
  const centerHadFocus = useRef(false);
  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      centerHadFocus.current = !!centerRef.current?.contains(event.target as Node);
    };
    const handleFocusOut = (event: FocusEvent) => {
      // A real blur to nowhere; a removed element's blur has a detached target and is ignored.
      if (event.relatedTarget === null && (event.target as Node).isConnected) {
        centerHadFocus.current = false;
      }
    };
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);
  const previousShowChip = useRef(showChip);
  useLayoutEffect(() => {
    if (!previousShowChip.current && showChip) {
      announce(t('sessionShown'));
      if (centerHadFocus.current) pillRef.current?.focus();
    }
    centerHadFocus.current = false;
    previousShowChip.current = showChip;
  }, [showChip, announce, t]);

  const logoLink = (
    <Link
      href={logoHref}
      onClick={handleLogoClick}
      className={logoLinkClasses}
      data-cy="header-logo-link"
    >
      {logo}
    </Link>
  );

  const chip = showChip && activeSessionIndicator && (
    <div data-cy="site-header-session-chip">
      <ActiveSessionIndicator ref={pillRef} {...activeSessionIndicator} />
    </div>
  );

  const signIn = !user && showLogin && (
    <Link href={loginHref} className={signInClasses} data-cy="site-header-sign-in">
      {t('signIn')}
    </Link>
  );

  const avatar = user && (
    <Button
      buttonType={ButtonType.text}
      ref={avatarRef}
      className={avatarButtonClasses}
      onClick={onAvatarClick}
      aria-label={tNav('userMenuFor', { name: user.name })}
      aria-expanded={isMenuOpen}
      aria-haspopup="true"
      data-cy="header-avatar-button"
    >
      <Avatar
        name={user.name}
        initials={user.initials}
        variant={AvatarVariant.accent}
        size={AvatarSize.small}
        isOutline={true}
      />
    </Button>
  );

  const zoneProps = {
    isWide,
    open,
    onOpenRequest: handleOpenRequest,
    onCloseRequest: handleCloseRequest,
  };

  let bar: React.ReactNode;
  if (hideOnPhone && !isDesktop) {
    bar = null;
  } else if (isLoading) {
    bar = (
      <header
        className={cn(siteHeaderBarClasses({ layout: 'skeleton' }), className)}
        data-cy="site-header-skeleton"
        aria-busy="true"
      >
        {logoLink}
      </header>
    );
  } else if (isDesktop) {
    bar = (
      <header
        className={cn(siteHeaderBarClasses({ layout: 'desktop' }), className)}
        data-cy="site-header"
        data-layout="CLASSIC"
      >
        <div className={leftClusterClasses}>
          {logoLink}
          <SiteHeaderZone zone="left" nodes={zones.left} {...zoneProps} />
        </div>
        <div className={centerSlotClasses}>
          {showChip ? (
            chip
          ) : (
            <SiteHeaderZone zone="center" nodes={zones.center} navRef={centerRef} {...zoneProps} />
          )}
        </div>
        <div className={rightClusterClasses}>
          <SiteHeaderZone zone="right" nodes={zones.right} {...zoneProps} />
          {signIn}
          {avatar}
        </div>
      </header>
    );
  } else {
    const long = activeSessionIndicator;
    const sessionText =
      showChip && long
        ? [long.desktopText, long.desktopGarageName, long.desktopTimeText]
            .filter(Boolean)
            .join(' · ')
        : undefined;
    bar = (
      <header
        className={cn(siteHeaderBarClasses({ layout: 'mobile' }), className)}
        data-cy="site-header"
        data-layout="CLASSIC"
      >
        <Button
          buttonType={ButtonType.text}
          ref={toggleRef}
          className={menuButtonClasses}
          aria-label={tSite(menuOpen ? 'closeMenu' : 'openMenu')}
          aria-expanded={menuOpen}
          aria-controls={drawerId}
          data-cy="site-header-menu-toggle"
          onClick={() => setMenuRequested((current) => !current)}
        >
          <MdMenu size={24} aria-hidden="true" />
        </Button>
        <div className={mobileCenterClasses}>{showChip ? chip : logoLink}</div>
        <div className="tw:flex tw:items-center tw:justify-end">
          {signIn}
          {avatar}
        </div>
        {menuOpen && (
          <SiteHeaderDrawer
            id={drawerId}
            logo={logo}
            logoHref={logoHref}
            zones={zones}
            isSignedIn={!!user}
            showLogin={showLogin}
            loginHref={loginHref}
            sessionText={sessionText}
            onSessionClick={activeSessionIndicator?.onClick}
            onClose={closeMenu}
          />
        )}
      </header>
    );
  }

  return (
    <>
      {bar}
      <ReleaseRefreshBanner />
      {!isLoading && activeSessionsPopup && (
        <ActiveSessionsPopup
          open={activeSessionsPopup.open}
          onClose={activeSessionsPopup.onClose}
          sessions={activeSessionsPopup.sessions}
          anchorRef={pillRef}
          onViewPass={handleViewPass}
        />
      )}
      {passReservation && (
        <ReservationPassModal
          open={!!passReservation}
          reservation={passReservation}
          variant="ticket"
          onClose={handleClosePass}
        />
      )}
    </>
  );
}
